import api from '@/api';
import * as dateutil from '@/util/date-helper.ts';

const convertAppointmentTypeToClient = function(value: number) {
	switch (value) {
		case 1: return 'Single';
		case 2: return 'RecurringMaster';
		case 3: return 'Occurrence';
		case 4: return 'Exception';
	}
	return 'Single';
}

const convertAppointmentTypeToServer = function(value: string) {
	switch (value) {
		case 'Single': return 1;
		case 'RecurringMaster': return 2;
		case 'Occurrence' : return 3;
		case 'Exception': return 4;
	}
	return 1;
}

const convertRecurrenceToServer = function(value: calendar.CalendarItemRecurrence) {
	let CalendarRecurrence = {
	RecurringType: value.ui_recurringType,
	StartDate: value.startDate,
	EndDate: value.ui_endType == '3' ? value.endDate : null,
	Interval: isNaN(Number(value.interval)) ? 1 : Number(value.interval),
	NumberOfOccurrences: value.ui_endType == '2' ? (isNaN(Number(value.numberOfOccurrences)) ? 0 : Number(value.numberOfOccurrences)) : null,
	FirstDayOfWeek: isNaN(Number(value.firstDayOfWeekValue)) ? 1 : Number(value.firstDayOfWeekValue),
	DayOfMonth: isNaN(Number(value.dayOfMonth)) ? 1 : Number(value.dayOfMonth),
	Month: isNaN(Number(value.monthValue)) ? 1 : Number(value.monthValue),
	DaysOfTheWeek_0: value.daysOfTheWeekArray?.indexOf(0) != -1,
	DaysOfTheWeek_1: value.daysOfTheWeekArray?.indexOf(1) != -1,
	DaysOfTheWeek_2: value.daysOfTheWeekArray?.indexOf(2) != -1,
	DaysOfTheWeek_3: value.daysOfTheWeekArray?.indexOf(3) != -1,
	DaysOfTheWeek_4: value.daysOfTheWeekArray?.indexOf(4) != -1,
	DaysOfTheWeek_5: value.daysOfTheWeekArray?.indexOf(5) != -1,
	DaysOfTheWeek_6: value.daysOfTheWeekArray?.indexOf(6) != -1,
	DaysOfTheWeek_7: value.daysOfTheWeekArray?.indexOf(7) != -1,
	DaysOfTheWeek_8: value.daysOfTheWeekArray?.indexOf(8) != -1,
	DaysOfTheWeek_9: value.daysOfTheWeekArray?.indexOf(9) != -1,
	};
	return CalendarRecurrence;
}

const convertRecurrenceToClient = function(value: any) {
	let recurrence:calendar.CalendarItemRecurrence = {
		ui_recurringType: value.recurringType,
		startDate: value.startDate,
		endDate: value.endDate,
		interval: value.interval,
		numberOfOccurrences: value.numberOfOccurrences,
		firstDayOfWeekValue: value.firstDayOfWeek,
		dayOfMonth: value.dayOfMonth,
		monthValue: value.month,
		hasEnd: value.endDate || value.numberOfOccurrences,
		daysOfTheWeekArray: [
			{flag: value.daysOfTheWeek_0, idx : 0},
			{flag: value.daysOfTheWeek_1, idx : 1},
			{flag: value.daysOfTheWeek_2, idx : 2},
			{flag: value.daysOfTheWeek_3, idx : 3},
			{flag: value.daysOfTheWeek_4, idx : 4},
			{flag: value.daysOfTheWeek_5, idx : 5},
			{flag: value.daysOfTheWeek_6, idx : 6},
			{flag: value.daysOfTheWeek_7, idx : 7},
			{flag: value.daysOfTheWeek_8, idx : 8},
			{flag: value.daysOfTheWeek_9, idx : 9},
		].filter((x) => x.flag).map((x) => x.idx)
	} as calendar.CalendarItemRecurrence;
	return recurrence;
}

const convertEventToClient = function(item: any):calendar.CalendarItem {
	let requiredAttendeesList = item.requiredAttendeesList?.length ? item.requiredAttendeesList : item.rRequiredAttendeesList;
	let optionalAttendeesList = item.optionalAttendeesList?.length ? item.optionalAttendeesList : item.rOptionalAttendeesList;
    let event: calendar.CalendarItem = {
        ...item,
		id: (item.id || item.ID).toString(),
		appointmentType: convertAppointmentTypeToClient(item.appointmentType || item.AppointmentType),
		subject: item.subject || item.Subject,
		location: item.location || item.Location,
		parentId: item.parentFolderID || item.ParentFolderID || null,
		parentFolderId: item.parentFolderID || item.ParentFolderID,
		start: ((item.startDate || item.StartDate) + '').replace('T', ' '),
		end: ((item.endDate || item.EndDate) + '').replace('T', ' '),
		organizer: {
			name: item.organizerName || item.OrganizerName,
			address: item.organizerAddress || item.OrganizerAddress,
		},
		importance: item.importance || item.Importance,
		isAllDayEvent : (item.isAllDayEvent || item.IsAllDayEvent) ? 'True' : 'False',
		isCancelled : item.isCancelled || item.IsCancelled,
		pidLidRecurrencePattern : item.recurringString || item.RecurringString,
		body: item.calendarEventBody?.body || item.CalendarEventBody?.body || item.calendarREventBody?.body || item.CalendarREventBody?.body,
		recurrence: convertRecurrenceToClient(item.calendarRecurrence || item.CalendarRecurrence),	
		requiredAttendees: requiredAttendeesList && requiredAttendeesList.length ? requiredAttendeesList.map((x:any) => { return {name: x.Name || x.name, address: x.Address || x.address} }) : [],
		optionalAttendees: optionalAttendeesList && optionalAttendeesList.length ? optionalAttendeesList.map((x:any) => { return {name: x.Name || x.name, address: x.Address || x.address} }) : [],
		attachments: item.attachments || item.Attachments,
		code: item.attachmentFileCode || item.AttachmentFileCode,
		resourceId: (item.resourceID ? item.resourceID.toString() : '') || item.ResourceID?.toString() || null,
		db_masterID: item.masterID || item.MasterID,
		db_selectID: item.selectGuid || item.SelectGuid,
		isSendMail: item.isSendMail || item.IsSendMail,
	} as calendar.CalendarItem;
    return event;
}

const convertEventToServer = function(item: calendar.CalendarItem) {	
    let event: any = {
		ID: isNaN(Number(item.id)) ? 0 : Number(item.id),
		MasterID: item.db_masterID,
		AppointmentType: convertAppointmentTypeToServer(item.appointmentType),
        Subject: item.subject,
		Location: item.location,
		ParentFolderID: Number(item.parentId || (item as any).ParentFolderId),
		StartDate: dateutil.uGetDateFormat('yyyy-MM-DD', dateutil.IEDate(item.start)) + 'T' + item.starttime,
		EndDate: dateutil.uGetDateFormat('yyyy-MM-DD', dateutil.IEDate(item.end)) + 'T' + item.endtime,
		IsAllDayEvent: item.isAllDayEvent,
		IsCancelled: item.isCancelled,
		RecurringString: item.pidLidRecurrencePattern,
		OrganizerName: item.organizer?.name,
		OrganizerAddress: item.organizer?.address,
		Importance: item.importance,
		CalendarEventBody: {
			Body: item.body,
		},
		CalendarRecurrence: convertRecurrenceToServer(item.recurrence),
		RequiredAttendeesList: item.requiredAttendees && item.requiredAttendees.length ? item.requiredAttendees.map((x) => { return {Name: x.name, Address: x.address} }) : [],
		OptionalAttendeesList: item.optionalAttendees && item.optionalAttendees.length ? item.requiredAttendees.map((x) => { return {Name: x.name, Address: x.address} }) : [],
		Attachments: item.attachments,
		AttachmentFileCode: item.code,
		ResourceID: isNaN(Number(item.resourceId)) ? null : (Number(item.resourceId) || null),
		IsDeleted: (item as any).IsDeleted || false,
		IsSendMail: item.isSendMail,
		DeletedAttachIDArray: (item as any).deletedAttachIDs,
	};
    return event;
}

export default {
    getCalendarEvent(item: any) {
        return api.post<any>({
            url: '/api/calendar/CalendarEvent/get?funcName=getCalendarEvent',
            data: {
				ID: item.ID,
				PersonCode: item.PersonCode,
				USE_TA_CALENDAR_EVENT_BODY: item.USE_TA_CALENDAR_EVENT_BODY || true,
				USE_TA_OPTIONAL_ATTENDEES: item.USE_TA_OPTIONAL_ATTENDEES || true,
				USE_TA_REQUIRED_ATTENDEES: item.USE_TA_REQUIRED_ATTENDEES || true,
				USE_TA_CALENDAR_RECURRENCE: item.USE_TA_CALENDAR_RECURRENCE || true,
				USE_TA_CALENDAR_RESOURCE: item.USE_TA_CALENDAR_RESOURCE || true,
				USE_TA_CALENDAR_FOLDER: item.USE_TA_CALENDAR_FOLDER || false,				
				USE_TA_CALENDAR_R_EVENT: item.USE_TA_CALENDAR_R_EVENT || false,
            }
        }).then((res) => {
		   res = convertEventToClient(res);
           return res;
        });
    },

    getAllCalendarEvent() {
        return api.post<Array<any>>({
            url: '/api/calendar/CalendarEvent/all?funcName=getAllCalendarEvent',
        }).then((res) => {
            return res.map((data) => {
				data = convertEventToClient(data);
                let result = {
                    ...data,
                };
                return result;
            });
        });
    },

    getFindCalendarEvent(item: any) {
        return api.post<Array<any>>({
            url: '/api/calendar/CalendarEvent/find?funcName=getFindCalendarEvent',
            data: {
				ID: item.ID || null,
				ParentFolderID: item.ParentFolderID || null,
				PersonCode: item.PersonCode || null,
				DeptCode: item.DeptCode || null,
				OrganizerName: item.OrganizerName || null,
				OrganizerAddress: item.OrganizerAddress || null,
				ResourceID: item.ResourceID || null,
				Subject: item.Subject || null,
				Location: item.Location || null,
				StartDate: item.StartDate || null,
				StartDateStart: item.StartDateStart || null,
				StartDateEnd: item.StartDateEnd || null,
				EndDate: item.EndDate || null,
				EndDateStart: item.EndDateStart || null,
				EndDateEnd: item.EndDateEnd || null,
				Importance: item.Importance || null,
				IsDeleted: item.IsDeleted || null,
				IsAllDayEvent: item.IsAllDayEvent || null,
				IsRecurring: item.IsRecurring || null,
				IsCancelled: item.IsCancelled || null,
				RecurringString: item.RecurringString || null,
				EumPageNumber: item.EumPageNumber || null,
				EumRowsPerPage: item.EumRowsPerPage || null,
				EumSearchCategory: item.EumSearchCategory || null,
				EumSearchKey: item.EumSearchKey || null,
				EumNoCondition: item.EumNoCondition || null,
				USE_TA_CALENDAR_EVENT_BODY: item.USE_TA_CALENDAR_EVENT_BODY || true,
				USE_TA_OPTIONAL_ATTENDEES: item.USE_TA_OPTIONAL_ATTENDEES || true,
				USE_TA_REQUIRED_ATTENDEES: item.USE_TA_REQUIRED_ATTENDEES || true,
				USE_TA_CALENDAR_RECURRENCE: item.USE_TA_CALENDAR_RECURRENCE || true,
				USE_TA_CALENDAR_FOLDER: item.USE_TA_CALENDAR_FOLDER || false,
				USE_TA_CALENDAR_RESOURCE: item.USE_TA_CALENDAR_RESOURCE || false,
				USE_TA_CALENDAR_R_EVENT: item.USE_TA_CALENDAR_R_EVENT || false,
            }
        }).then((res) => {
            return res.map((data) => {
				let newItem = convertEventToClient(data);
                let result = {
                    ...newItem,
                };
                return result;
            });
        });
    },

    createCalendarEvent(item: any) {
		item = convertEventToServer(item);
        return api.post<any>({
            url: '/api/calendar/CalendarEvent/create?funcName=createCalendarEvent',
            data: {
				ParentFolderID: item.ParentFolderID,
				AppointmentType: item.AppointmentType,
				AttachmentFileCode: item.AttachmentFileCode,
				PersonCode: item.PersonCode,
				DeptCode: item.DeptCode,
				OrganizerName: item.OrganizerName,
				OrganizerAddress: item.OrganizerAddress,
				ResourceID: item.ResourceID,
				Subject: item.Subject,
				Location: item.Location,
				StartDate: item.StartDate,
				EndDate: item.EndDate,
				Importance: item.Importance,
				IsDeleted: item.IsDeleted,
				IsAllDayEvent: item.IsAllDayEvent,
				IsRecurring: item.IsRecurring,
				IsCancelled: item.IsCancelled,
				IsSendMail: item.IsSendMail,
				RecurringString: item.RecurringString,
				CreatedId: item.CreatedId,
				UpdatedId: item.UpdatedId,
				CreatedDate: item.CreatedDate,
				UpdatedDate: item.UpdatedDate,
				CalendarEventBody: item.CalendarEventBody,
				CalendarRecurrence: item.CalendarRecurrence,
				OptionalAttendeesList: item.OptionalAttendeesList || [],
				RequiredAttendeesList: item.RequiredAttendeesList || [],
				CalendarFolder: item.CalendarFolder,
				CalendarResource: item.CalendarResource,
				USE_TA_CALENDAR_EVENT_BODY: item.USE_TA_CALENDAR_EVENT_BODY || true,
				USE_TA_OPTIONAL_ATTENDEES: item.USE_TA_OPTIONAL_ATTENDEES || true,
				USE_TA_REQUIRED_ATTENDEES: item.USE_TA_REQUIRED_ATTENDEES || true,
				USE_TA_CALENDAR_RECURRENCE: item.USE_TA_CALENDAR_RECURRENCE || true,
				USE_TA_CALENDAR_RESOURCE: item.USE_TA_CALENDAR_RESOURCE || true,
				USE_TA_CALENDAR_FOLDER: item.USE_TA_CALENDAR_FOLDER || false,				
				USE_TA_CALENDAR_R_EVENT: item.USE_TA_CALENDAR_R_EVENT || false,
            }
        });
    },

    updateCalendarEvent(item: any) {
		item = convertEventToServer(item);
        return api.post<any>({
            url: '/api/calendar/CalendarEvent/update?funcName=updateCalendarEvent',
            data: {
				ID: item.ID,
				ParentFolderID: item.ParentFolderID,
				AppointmentType: item.AppointmentType,
				AttachmentFileCode: item.AttachmentFileCode,
				PersonCode: item.PersonCode,
				DeptCode: item.DeptCode,
				OrganizerName: item.OrganizerName,
				OrganizerAddress: item.OrganizerAddress,
				ResourceID: item.ResourceID,
				Subject: item.Subject,
				Location: item.Location,
				StartDate: item.StartDate,
				EndDate: item.EndDate,
				Importance: item.Importance,
				IsDeleted: item.IsDeleted,
				IsAllDayEvent: item.IsAllDayEvent,
				IsRecurring: item.IsRecurring,
				IsCancelled: item.IsCancelled,
				IsSendMail: item.IsSendMail,
				RecurringString: item.RecurringString,
				CreatedId: item.CreatedId,
				UpdatedId: item.UpdatedId,
				CreatedDate: item.CreatedDate,
				UpdatedDate: item.UpdatedDate,
				CalendarEventBody: item.CalendarEventBody,
				CalendarRecurrence: item.CalendarRecurrence,
				OptionalAttendeesList: item.OptionalAttendeesList || [],
				RequiredAttendeesList: item.RequiredAttendeesList || [],
				CalendarFolder: item.CalendarFolder,
				CalendarResource: item.CalendarResource,
				DeletedAttachIDArray: item.DeletedAttachIDArray,
				USE_TA_CALENDAR_EVENT_BODY: item.USE_TA_CALENDAR_EVENT_BODY || true,
				USE_TA_OPTIONAL_ATTENDEES: item.USE_TA_OPTIONAL_ATTENDEES || true,
				USE_TA_REQUIRED_ATTENDEES: item.USE_TA_REQUIRED_ATTENDEES || true,
				USE_TA_CALENDAR_RECURRENCE: item.USE_TA_CALENDAR_RECURRENCE || true,
				USE_TA_CALENDAR_RESOURCE: item.USE_TA_CALENDAR_RESOURCE || true,
				USE_TA_CALENDAR_FOLDER: item.USE_TA_CALENDAR_FOLDER || false,				
				USE_TA_CALENDAR_R_EVENT: item.USE_TA_CALENDAR_R_EVENT || false,
            }
        });
    },

    deleteCalendarEvent(item: any) {
		item = convertEventToServer(item);
        return api.post<any>({
            url: '/api/calendar/CalendarEvent/delete?funcName=deleteCalendarEvent',
            data: {
				ID: item.ID,
				USE_TA_CALENDAR_EVENT_BODY: item.USE_TA_CALENDAR_EVENT_BODY || true,
				USE_TA_OPTIONAL_ATTENDEES: item.USE_TA_OPTIONAL_ATTENDEES || true,
				USE_TA_REQUIRED_ATTENDEES: item.USE_TA_REQUIRED_ATTENDEES || true,
				USE_TA_CALENDAR_RECURRENCE: item.USE_TA_CALENDAR_RECURRENCE || true,
				USE_TA_CALENDAR_FOLDER: item.USE_TA_CALENDAR_FOLDER || false,
				USE_TA_CALENDAR_RESOURCE: item.USE_TA_CALENDAR_RESOURCE || false,
				USE_TA_CALENDAR_R_EVENT: item.USE_TA_CALENDAR_R_EVENT || false,
            }
        });
	},
	
	getCalendarEventBody(item: any) {
        return api.post<any>({
            url: '/api/calendar/CalendarEventBody/get?funcName=getCalendarEventBody',
            data: {
				EventID: item.EventID,
				USE_TA_CALENDAR_EVENT: item.USE_TA_CALENDAR_EVENT || false,

            }
        }).then((res) => {
           return res;
        });
	},
	



	getRCalendarEvent(item: any) {
        return api.post<any>({
            url: '/api/calendar/CalendarREvent/get?funcName=getRCalendarEvent',
            data: {
				ID: item.ID,
				PersonCode: item.PersonCode,
				USE_TA_CALENDAR_R_EVENT_BODY: item.USE_TA_CALENDAR_EVENT_BODY || true,
				USE_TA_R_OPTIONAL_ATTENDEES: item.USE_TA_OPTIONAL_ATTENDEES || true,
				USE_TA_R_REQUIRED_ATTENDEES: item.USE_TA_REQUIRED_ATTENDEES || true,
				USE_TA_CALENDAR_RECURRENCE: item.USE_TA_CALENDAR_RECURRENCE || true,
				USE_TA_CALENDAR_RESOURCE: item.USE_TA_CALENDAR_RESOURCE || true,
            }
        }).then((res) => {
		   res = convertEventToClient(res);
           return res;
        });
    },

	createRCalendarEvent(item: any) {
		item = convertEventToServer(item);
        return api.post<any>({
            url: '/api/calendar/CalendarREvent/create?funcName=createRCalendarEvent',
            data: {
				ID: item.ID,
				MasterID: item.MasterID,
				ParentFolderID: item.ParentFolderID,
				AppointmentType: item.AppointmentType,
				AttachmentFileCode: item.AttachmentFileCode,
				PersonCode: item.PersonCode,
				DeptCode: item.DeptCode,
				OrganizerName: item.OrganizerName,
				OrganizerAddress: item.OrganizerAddress,
				ResourceID: item.ResourceID,
				Subject: item.Subject,
				Location: item.Location,
				StartDate: item.StartDate,
				EndDate: item.EndDate,
				Importance: item.Importance,
				IsDeleted: item.IsDeleted,
				IsAllDayEvent: item.IsAllDayEvent,
				IsRecurring: item.IsRecurring,
				IsCancelled: item.IsCancelled,
				IsSendMail: item.IsSendMail,
				RecurringString: item.RecurringString,
				CreatedId: item.CreatedId,
				UpdatedId: item.UpdatedId,
				CreatedDate: item.CreatedDate,
				UpdatedDate: item.UpdatedDate,
				CalendarREventBody: item.CalendarEventBody,
				CalendarRecurrence: item.CalendarRecurrence,
				ROptionalAttendeesList: item.OptionalAttendeesList || [],
				RRequiredAttendeesList: item.RequiredAttendeesList || [],
				CalendarFolder: item.CalendarFolder,
				CalendarResource: item.CalendarResource,
				USE_TA_CALENDAR_R_EVENT_BODY: item.USE_TA_CALENDAR_EVENT_BODY || true,
				USE_TA_R_OPTIONAL_ATTENDEES: item.USE_TA_OPTIONAL_ATTENDEES || true,
				USE_TA_R_REQUIRED_ATTENDEES: item.USE_TA_REQUIRED_ATTENDEES || true,
				USE_TA_CALENDAR_RESOURCE: item.USE_TA_CALENDAR_RESOURCE || true,
            }
        });
    },

    updateRCalendarEvent(item: any) {
		item = convertEventToServer(item);
        return api.post<any>({
            url: '/api/calendar/CalendarREvent/update?funcName=updateRCalendarEvent',
            data: {
				ID: item.ID,
				MasterID: item.MasterID,
				ParentFolderID: item.ParentFolderID,
				AppointmentType: item.AppointmentType,
				AttachmentFileCode: item.AttachmentFileCode,
				PersonCode: item.PersonCode,
				DeptCode: item.DeptCode,
				OrganizerName: item.OrganizerName,
				OrganizerAddress: item.OrganizerAddress,
				ResourceID: item.ResourceID,
				Subject: item.Subject,
				Location: item.Location,
				StartDate: item.StartDate,
				EndDate: item.EndDate,
				Importance: item.Importance,
				IsDeleted: item.IsDeleted,
				IsAllDayEvent: item.IsAllDayEvent,
				IsRecurring: item.IsRecurring,
				IsCancelled: item.IsCancelled,
				IsSendMail: item.IsSendMail,
				RecurringString: item.RecurringString,
				CreatedId: item.CreatedId,
				UpdatedId: item.UpdatedId,
				CreatedDate: item.CreatedDate,
				UpdatedDate: item.UpdatedDate,
				CalendarREventBody: item.CalendarEventBody,
				CalendarRecurrence: item.CalendarRecurrence,
				ROptionalAttendeesList: item.OptionalAttendeesList || [],
				RRequiredAttendeesList: item.RequiredAttendeesList || [],
				CalendarFolder: item.CalendarFolder,
				CalendarResource: item.CalendarResource,
				DeletedAttachIDArray: item.DeletedAttachIDArray,
				USE_TA_CALENDAR_R_EVENT_BODY: item.USE_TA_CALENDAR_EVENT_BODY || true,
				USE_TA_R_OPTIONAL_ATTENDEES: item.USE_TA_OPTIONAL_ATTENDEES || true,
				USE_TA_R_REQUIRED_ATTENDEES: item.USE_TA_REQUIRED_ATTENDEES || true,
				USE_TA_CALENDAR_RECURRENCE: item.USE_TA_CALENDAR_RECURRENCE || true,
				USE_TA_CALENDAR_RESOURCE: item.USE_TA_CALENDAR_RESOURCE || true,
            }
        });
    },

    deleteRCalendarEvent(item: any) {
		item = convertEventToServer(item);
        return api.post<any>({
            url: '/api/calendar/CalendarREvent/delete?funcName=deleteRCalendarEvent',
            data: {
				ID: item.ID,
				USE_TA_CALENDAR_R_EVENT_BODY: item.USE_TA_CALENDAR_EVENT_BODY || true,
				USE_TA_R_OPTIONAL_ATTENDEES: item.USE_TA_OPTIONAL_ATTENDEES || true,
				USE_TA_R_REQUIRED_ATTENDEES: item.USE_TA_REQUIRED_ATTENDEES || true,
            }
        });
	},
}
